import { VerifyResetPasswordRequest } from './../dto/verify-reset-password-request';
import { ResetPasswordRequest } from './../dto/reset-password-request';
import { VerifyEmailRequest } from './../dto/verify-email-request';
import { ConfiguredPermissions } from './../dto/configured-permissions';
import { SaveUserRequest } from '../dto/save-user-request';
import { UserData } from '../dto/user-data';
import { AuthorizationInfo } from '../dto/authorization-info';
import { SecurityInfo } from '../dto/security-info';
import { Injectable, Inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { retry } from 'rxjs/operators';
import { Customer } from '../dto/customer';
import { SecurableActivity } from '../dto/securable-activity';
import { SavePermissionsResponse } from '../dto/save-permissions-response';
import { SecuritySettings } from '../dto/security-settings';
import { RegisterUserRequest } from '../dto/register-user-request';
import { RegistrationResponse } from '../dto/registration-response.enum';
import { API_SERVER } from '@pcplus/domain-core';

@Injectable({
  providedIn: 'root'
})
export class SecurityService {
  constructor(
    private http: HttpClient,
    @Inject(API_SERVER) private apiServer: string
  ) {}

  getSecurityInfo(customer: string) {
    return this.http
      .get<SecurityInfo>(
        this.apiServer + `api/Authorization/securityInfo/${customer}`
      )
      .pipe(retry(3));
  }

  getUsers(customer: string) {
    return this.http
      .get<UserData[]>(this.apiServer + `api/Authorization/users/${customer}`)
      .pipe(retry(3));
  }

  getAuthorizationInfo() {
    return this.http
      .get<AuthorizationInfo>(
        this.apiServer + 'api/Authorization/authorizationInfo'
      )
      .pipe(retry(3));
  }

  deleteUser(customerId: string, id: string) {
    return this.http.delete(
      `${this.apiServer}api/Authorization/users/${customerId}/${id}`
    );
  }

  isUsernameTaken(username: string) {
    return this.http.get<boolean>(
      `${this.apiServer}api/Authorization/user/${username}`
    );
  }

  createUser(user: UserData, password: string) {
    return this.http.put<UserData>(
      this.apiServer + 'api/Authorization/users',
      new SaveUserRequest(user, password)
    );
  }

  saveUser(user: UserData, password: string) {
    return this.http.post<boolean>(
      this.apiServer + 'api/Authorization/users',
      new SaveUserRequest(user, password)
    );
  }

  deleteCustomer(id: string) {
    return this.http.delete(this.apiServer + `api/Authorization/${id}/`);
  }

  saveCustomer(id: string, name: string) {
    return this.http.post<string>(
      `${this.apiServer}api/Authorization/customers/${id}/${name}`,
      null
    );
  }

  getCustomerName(id: string) {
    return this.http
      .get<string>(this.apiServer + `api/Authorization/customers/${id}`)
      .pipe(retry(3));
  }

  addGroup(name: string, customer: string) {
    return this.http.put<string>(
      `${this.apiServer}api/Authorization/${name}/${customer}`,
      null
    );
  }

  addUserToGroup(customerId: string, groupID: string, userID: string) {
    return this.http.put(
      `${this.apiServer}api/Authorization/userGroups/${customerId}/${groupID}/${userID}`,
      null
    );
  }

  removeUserFromGroup(customerId: string, groupID: string, userID: string) {
    return this.http.delete(
      `${this.apiServer}api/Authorization/${customerId}/${groupID}/${userID}`
    );
  }

  deleteGroup(customerId: string, id: string) {
    return this.http.delete(
      this.apiServer + `api/Authorization/${customerId}/${id}`
    );
  }

  saveSecuritySettings(settings: SecuritySettings) {
    return this.http.post(
      `${this.apiServer}api/Authorization/securitySettings`,
      settings
    );
  }

  renameGroup(customerId: string, id: string, name: string) {
    return this.http.post(
      `${this.apiServer}api/Authorization/${customerId}/${id}/${name}`,
      null
    );
  }

  getAllCustomers() {
    return this.http
      .get<Customer[]>(this.apiServer + 'api/Authorization/customers')
      .pipe(retry(3));
  }

  getDynamicSecurables(customerId: string, activity: string[]) {
    return this.http.post<SecurableActivity[]>(
      `${this.apiServer}api/Authorization/dynamicSecurables/${customerId}`,
      activity
    );
  }

  savePermissions(permissions: ConfiguredPermissions) {
    return this.http.post<SavePermissionsResponse>(
      `${this.apiServer}api/Authorization/permissions`,
      permissions
    );
  }

  registerUser(request: RegisterUserRequest) {
    return this.http.post<RegistrationResponse>(
      `${this.apiServer}api/Authorization/register`,
      request
    );
  }

  verifyEmail(request: VerifyEmailRequest) {
    return this.http.post<RegistrationResponse>(
      `${this.apiServer}api/Authorization/verify`,
      request
    );
  }

  resetUserPassword(request: ResetPasswordRequest) {
    return this.http.post<RegistrationResponse>(
      `${this.apiServer}api/Authorization/reset`,
      request
    );
  }

  resetUserPasswordVerifyEmail(request: VerifyResetPasswordRequest) {
    return this.http.post<RegistrationResponse>(
      `${this.apiServer}api/Authorization/verifyReset`,
      request
    );
  }
}
